/**
 * Every collection slug the site serves -- the `<slug>` segment in `/collections/<slug>/`.
 *
 * Single source of truth: `COLLECTIONS` is keyed by these, so adding a member without a config
 * is a compile error, and callers and tests never hardcode the string.
 */
export enum CollectionSlug {
    Jewelry = 'jewelry',
    Paintings = 'paintings',
    StarWars = 'star-wars',
    Tabletop = 'tabletop',
    TraditionalAntique = 'traditional-antique',
    TwentiethCenturyDesign = '20th-century-design',
}

/**
 * Which page treatment a collection renders. The two values are not interchangeable styles --
 * they are two generations of the page, and `LegacyPromo` is frozen.
 */
export enum CollectionLayout {
    /**
     * The LAAC-2371 design: flat hero, unlabelled circle carousel, no related chips, and the
     * shared auction house exclusions applied.
     */
    Collection = 'collection',
    /**
     * The original May-4th Star Wars page, preserved as-is: hammer-ui STAR_WARS banner, headed
     * circle row, related chips, and no house exclusions.
     */
    LegacyPromo = 'legacy-promo',
}

/** One crumb in the trail rendered after the "Home" crumb the component prepends. */
export type CollectionBreadcrumb = {
    /** Empty for the current page, which is how the search API's own breadcrumbs render it. */
    href: string;
    label: string;
};

/** One circle in the sub-category carousel. */
export type CollectionSubcategory = {
    /** Destination, exactly as merch specified it (already carries its own filters). */
    href: string;
    /**
     * Absolute URL on the LiveAuctioneers image CDN. `addImageParams` appends resize params, which
     * that CDN honours -- a 1.3MB source comes back around 38KB at the 220px the tile renders at.
     */
    imageUrl: string;
    title: string;
};

export type CollectionConfig = {
    breadcrumbs: CollectionBreadcrumb[];
    /** Hero sub-heading. */
    description: string;
    /**
     * Plural noun for the hero's "Browse {n} upcoming {itemNoun} for sale" line. Omit for the
     * generic "items", which is what every collection said before LAAC-2372.
     */
    itemNoun?: string;
    layout: CollectionLayout;
    /** <meta name="description">. Omit to keep the search-derived description. */
    metaDescription?: string;
    /**
     * Search-URL parameters that define this collection's results, in the same shape
     * `parseQueryParams` produces. Merged as DEFAULTS under the real URL params, so
     * filter/sort/page navigation still wins and the defining filters never have to
     * appear in the address bar.
     */
    searchParams: Record<string, string>;
    /** <title>, without the " | LiveAuctioneers" suffix. Omit to keep the search-derived title. */
    seoTitle?: string;
    /** URL segment: /collections/<slug>/ */
    slug: CollectionSlug;
    subcategories: CollectionSubcategory[];
    /** H2 above the circles. Omit for the LAAC-2371 design, which has no heading. */
    subcategoriesHeading?: string;
    /** Hero H1. */
    title: string;
};
